/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  withStyles,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { formatDateGeneric } from 'utilities/common';
import { formatMoney } from 'utilities/currencyFormatter';

import { getApprovedLineItemReports } from '../store/expenses.actions';
import { GET_APPROVED_LINE_ITEM_REPORTS_FAILURE } from '../store/types';

const styles = theme => ({
  container: { padding: theme.spacing(2) },
  filtersRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  filtersLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  filterControl: { minWidth: 240 },
  tableWrap: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  rowHeader: {
    display: 'grid',
    gridTemplateColumns: '120px 120px 120px 90px 150px 150px 150px 120px 120px',
    gap: theme.spacing(1),
    width: '100%',
    alignItems: 'center'
  },
  headerLabel: { fontWeight: 700, fontSize: '0.78rem' },
  headerValue: {
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  accordion: {
    marginBottom: 0,
    boxShadow: 'none',
    border: 0,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '0 !important',
    backgroundColor: 'transparent',
    transition: 'background-color 120ms ease',
    '&:before': { display: 'none' },
    '&:hover': { backgroundColor: '#f7fbfc' },
    '&.Mui-expanded': { backgroundColor: '#f3f9fb' },
    '&.Mui-expanded:hover': { backgroundColor: '#eaf4f8' }
  },
  summary: {
    minHeight: '40px !important',
    '& .MuiAccordionSummary-content': { margin: '6px 0 !important' }
  },
  details: {
    paddingTop: 0,
    borderTop: `1px solid ${theme.palette.grey[300]}`
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))',
    gap: theme.spacing(1.25),
    width: '100%'
  },
  detailFieldLabel: { fontSize: '0.72rem', color: theme.palette.text.secondary },
  detailFieldValue: { fontSize: '0.8rem', wordBreak: 'break-word' },
  stickyHeader: {
    background: '#f7f7f7',
    border: 0,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 0,
    padding: theme.spacing(0.75, 1),
    marginBottom: theme.spacing(0.5)
  },
  noData: { padding: theme.spacing(6), textAlign: 'center' }
});

const money = v => formatMoney(v == null || v === '' ? 0 : v);
const dt = v => (v ? formatDateGeneric(v) : '-');

const Field = ({ classes, label, value }) => (
  <Box>
    <Typography className={classes.detailFieldLabel}>{label}</Typography>
    <Typography className={classes.detailFieldValue}>{value ?? ''}</Typography>
  </Box>
);

const ApprovedLineItemSummariesView = props => {
  const { classes, summaries, setPageTitle, getApprovedLineItemReports, showToast } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const [importStatusFilter, setImportStatusFilter] = useState([]);
  const [processStatusFilter, setProcessStatusFilter] = useState([]);
  const [payToFilter, setPayToFilter] = useState([]); // E / V

  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportImportStatuses, setExportImportStatuses] = useState([]);
  const [exportProcessStatuses, setExportProcessStatuses] = useState([]);
  const [exportPayTo, setExportPayTo] = useState([]);

  const sortedSummaries = useMemo(() => {
    if (!Array.isArray(summaries)) return [];
    return [...summaries].sort((a, b) => new Date(b.addedOn) - new Date(a.addedOn));
  }, [summaries]);

  useEffect(() => {
    setPageTitle('Approved Line Item Summaries');
    (async () => {
      try {
        const resp = await getApprovedLineItemReports({});
        if (resp?.type === GET_APPROVED_LINE_ITEM_REPORTS_FAILURE) {
          showToast('Failed to retrieve approved line item summaries.', {
            severity: TOAST_MESSAGE_SEVERITY_ERROR
          });
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const importStatusOptions = useMemo(() => {
    const map = new Map();
    (sortedSummaries || []).forEach(r => {
      const id = String(r.acumaticaImportStatusId ?? '');
      if (!id) return;
      map.set(id, r.acumaticaImportStatusName || id);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [sortedSummaries]);

  const processStatusOptions = useMemo(() => {
    const map = new Map();
    (sortedSummaries || []).forEach(r => {
      const id = String((r.acumaticaBillStatusId || '').trim());
      if (!id) return;
      map.set(id, r.acumaticaBillStatusName || id);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [sortedSummaries]);

  const payToOptions = useMemo(() => {
    const vals = new Set((sortedSummaries || []).map(r => (r.payTo || '').trim()).filter(Boolean));
    return Array.from(vals)
      .sort()
      .map(v => ({ id: v, name: v }));
  }, [sortedSummaries]);

  const filteredRows = useMemo(() => {
    return (sortedSummaries || []).filter(r => {
      const importId = String(r.acumaticaImportStatusId ?? '');
      const processId = String((r.acumaticaBillStatusId || '').trim());
      const payTo = String((r.payTo || '').trim());

      const importOk = importStatusFilter.length === 0 || importStatusFilter.includes(importId);
      const processOk = processStatusFilter.length === 0 || processStatusFilter.includes(processId);
      const payToOk = payToFilter.length === 0 || payToFilter.includes(payTo);

      return importOk && processOk && payToOk;
    });
  }, [sortedSummaries, importStatusFilter, processStatusFilter, payToFilter]);

  const exportRows = useMemo(() => {
    return (sortedSummaries || []).filter(r => {
      const importId = String(r.acumaticaImportStatusId ?? '');
      const processId = String((r.acumaticaBillStatusId || '').trim());
      const payTo = String((r.payTo || '').trim());

      const importOk = exportImportStatuses.length === 0 || exportImportStatuses.includes(importId);
      const processOk = exportProcessStatuses.length === 0 || exportProcessStatuses.includes(processId);
      const payToOk = exportPayTo.length === 0 || exportPayTo.includes(payTo);

      return importOk && processOk && payToOk;
    });
  }, [sortedSummaries, exportImportStatuses, exportProcessStatuses, exportPayTo]);

  const exportCsv = rows => {
    const columns = [
      'approvedLineItemId',
      'sourceType',
      'sourceId',
      'authId',
      'payAmount',
      'lineItemApprovedAmount',
      'payTo',
      'addedOn',
      'mtTaxEngineCalculatedOn',
      'acumaticaPulledOn',
      'apPostedDate',
      'arPostedDate',
      'acumaticaImportStatusId',
      'acumaticaImportStatusName',
      'acumaticaBillStatusId',
      'acumaticaBillStatusName',
      'payeePrimaryKey',
      'acumaticaReferenceNumber'
    ];
    const esc = v => (v == null ? '' : `"${String(v).replace(/"/g, '""')}"`);
    const lines = [columns.join(','), ...rows.map(r => columns.map(c => esc(r[c])).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `approved-line-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openExportDialog = () => {
    setExportImportStatuses(importStatusFilter);
    setExportProcessStatuses(processStatusFilter);
    setExportPayTo(payToFilter);
    setShowExportDialog(true);
  };

  const runExport = () => {
    exportCsv(exportRows);
    setShowExportDialog(false);
  };

  if (isLoading) return <FullscreenSpinner />;

  return (
    <Box className={classes.container}>
      <Box className={classes.filtersRow}>
        <Box className={classes.filtersLeft}>
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Import Status</InputLabel>
            <Select
              multiple
              value={importStatusFilter}
              onChange={e => setImportStatusFilter(e.target.value)}
              label="Import Status"
              renderValue={selected =>
                importStatusOptions
                  .filter(x => selected.includes(x.id))
                  .map(x => x.name)
                  .join(', ')
              }
            >
              {importStatusOptions.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>
                  <Checkbox checked={importStatusFilter.includes(opt.id)} />
                  <ListItemText primary={opt.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Acumatica Process Status</InputLabel>
            <Select
              multiple
              value={processStatusFilter}
              onChange={e => setProcessStatusFilter(e.target.value)}
              label="Acumatica Process Status"
              renderValue={selected =>
                processStatusOptions
                  .filter(x => selected.includes(x.id))
                  .map(x => x.name)
                  .join(', ')
              }
            >
              {processStatusOptions.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>
                  <Checkbox checked={processStatusFilter.includes(opt.id)} />
                  <ListItemText primary={opt.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Pay To</InputLabel>
            <Select
              multiple
              value={payToFilter}
              onChange={e => setPayToFilter(e.target.value)}
              label="Pay To"
              renderValue={selected => selected.join(', ')}
            >
              {payToOptions.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>
                  <Checkbox checked={payToFilter.includes(opt.id)} />
                  <ListItemText primary={opt.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Button variant="contained" color="primary" onClick={openExportDialog}>
          Export CSV
        </Button>
      </Box>

      <Box className={classes.tableWrap}>
        <Paper className={classes.stickyHeader}>
          <Box className={classes.rowHeader}>
            <Typography className={classes.headerLabel}>Approved Date</Typography>
            <Typography className={classes.headerLabel}>Auth ID</Typography>
            <Typography className={classes.headerLabel}>Pay Amount</Typography>
            <Typography className={classes.headerLabel}>Pay To</Typography>
            <Typography className={classes.headerLabel}>Acu. Status</Typography>
            <Typography className={classes.headerLabel}>MT Tax Calc</Typography>
            <Typography className={classes.headerLabel}>Acumatica Pull</Typography>
            <Typography className={classes.headerLabel}>AP Posted</Typography>
            <Typography className={classes.headerLabel}>AR Posted</Typography>
          </Box>
        </Paper>

        {filteredRows.length === 0 ? (
          <Paper className={classes.noData}>
            <Typography>No records were returned. Check filters above.</Typography>
          </Paper>
        ) : (
          filteredRows.map(row => {
            const expanded = expandedRowId === row.approvedLineItemId;
            return (
              <Accordion
                key={`${row.sourceType}-${row.sourceId}-${row.approvedLineItemId}`}
                expanded={expanded}
                onChange={() => setExpandedRowId(expanded ? null : row.approvedLineItemId)}
                className={classes.accordion}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.summary}>
                  <Box className={classes.rowHeader}>
                    <Typography className={classes.headerValue}>{dt(row.addedOn)}</Typography>
                    <Typography className={classes.headerValue}>{row.authId || ''}</Typography>
                    <Typography className={classes.headerValue}>{money(row.payAmount)}</Typography>
                    <Typography className={classes.headerValue}>{row.payTo || '-'}</Typography>
                    <Typography className={classes.headerValue}>{row.acumaticaBillStatusName || '-'}</Typography>
                    <Typography className={classes.headerValue}>{dt(row.mtTaxEngineCalculatedOn)}</Typography>
                    <Typography className={classes.headerValue}>{dt(row.acumaticaPulledOn)}</Typography>
                    <Typography className={classes.headerValue}>{dt(row.apPostedDate)}</Typography>
                    <Typography className={classes.headerValue}>{dt(row.arPostedDate)}</Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails className={classes.details}>
                  <Divider />
                  <Box mt={1.5} className={classes.detailGrid}>
                    <Field classes={classes} label="Auth ID" value={row.authId} />
                    <Field classes={classes} label="Pay Amount" value={money(row.payAmount)} />
                    <Field classes={classes} label="Pay To" value={row.payTo} />
                    <Field classes={classes} label="Acumatica Status" value={row.acumaticaBillStatusName} />

                    <Field classes={classes} label="MT Tax Engine Calculated On" value={dt(row.mtTaxEngineCalculatedOn)} />
                    <Field classes={classes} label="Acumatica Pulled On" value={dt(row.acumaticaPulledOn)} />
                    <Field classes={classes} label="AP Posted Date" value={dt(row.apPostedDate)} />
                    <Field classes={classes} label="AR Posted Date" value={dt(row.arPostedDate)} />

                    <Field classes={classes} label="Import Status" value={row.acumaticaImportStatusName} />
                    {/* <Field classes={classes} label="Acumatica Status" value={row.acumaticaBillStatusName} /> */}
                    <Field classes={classes} label="Approved Line Item ID" value={row.approvedLineItemId} />
                    <Field classes={classes} label="EE/Partner Id" value={row.payeePrimaryKey} />

                    <Field classes={classes} label="Reference" value={row.reference} />
                    <Field classes={classes} label="AP Line Description" value={row.apLineDescription} />
                    <Field classes={classes} label="Acumatica Ref #" value={row.acumaticaReferenceNumber} />
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>

      <Dialog open={showExportDialog} onClose={() => setShowExportDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Export Approved Line Items</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Import Status</InputLabel>
              <Select
                multiple
                value={exportImportStatuses}
                onChange={e => setExportImportStatuses(e.target.value)}
                label="Import Status"
                renderValue={selected =>
                  importStatusOptions
                    .filter(x => selected.includes(x.id))
                    .map(x => x.name)
                    .join(', ')
                }
              >
                {importStatusOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    <Checkbox checked={exportImportStatuses.includes(opt.id)} />
                    <ListItemText primary={opt.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Acumatica Process Status</InputLabel>
              <Select
                multiple
                value={exportProcessStatuses}
                onChange={e => setExportProcessStatuses(e.target.value)}
                label="Acumatica Process Status"
                renderValue={selected =>
                  processStatusOptions
                    .filter(x => selected.includes(x.id))
                    .map(x => x.name)
                    .join(', ')
                }
              >
                {processStatusOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    <Checkbox checked={exportProcessStatuses.includes(opt.id)} />
                    <ListItemText primary={opt.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Pay To</InputLabel>
              <Select
                multiple
                value={exportPayTo}
                onChange={e => setExportPayTo(e.target.value)}
                label="Pay To"
                renderValue={selected => selected.join(', ')}
              >
                {payToOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    <Checkbox checked={exportPayTo.includes(opt.id)} />
                    <ListItemText primary={opt.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2">{`Records to export: ${exportRows.length}`}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportDialog(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={runExport} disabled={exportRows.length === 0}>
            Export CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = state => ({
  summaries: state.expenses?.approvedLineItemReports || []
});

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    setPageTitle,
    showToast,
    getApprovedLineItemReports
  })
)(ApprovedLineItemSummariesView);
